import * as React from 'react';
import { useHistory } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  IconButton,
} from '@material-ui/core';
import { Edit, Payment } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import PaginationData from 'types/models/PaginationData';
import LuxuryBox from 'types/models/LuxuryBox';
import { getLuxuryBoxes } from 'services/venuesServices';
import AppContext from 'contexts/AppContext';
import EditVenueContext from 'contexts/EditVenueContext';
import BooleanIcon from 'components/common/BooleanIcon';
import { formatMoneyAuto } from 'utils/formatMoney';

export default (): JSX.Element => {
  const history = useHistory();
  const [paginationLuxuryBox, setPaginationLuxuryBox] = React.useState<PaginationData<LuxuryBox>>();
  const { currentPageListNumber, setCurrentPageListNumber } = React.useContext(AppContext);
  const { setSelectedLuxuryBox, selectedLuxuryBox } = React.useContext(EditVenueContext);
  const { selectedVenue } = React.useContext(EditVenueContext);
  const { t } = useTranslation();

  const loadLuxuryBoxes = React.useCallback(
    async (venueId: string, page?: number) => {
      const paginationData = await getLuxuryBoxes(venueId, page);
      if (paginationData) {
        setSelectedLuxuryBox(null);
        setPaginationLuxuryBox(paginationData);
      }
    },
    [selectedLuxuryBox],
  );

  React.useEffect(() => {
    if (selectedVenue) {
      loadLuxuryBoxes(selectedVenue.id);
    } else {
      history.push('/private/venues');
    }
  }, []);

  React.useEffect(() => {
    if (currentPageListNumber && selectedVenue) {
      loadLuxuryBoxes(selectedVenue?.id, selectedLuxuryBox ? 1 : currentPageListNumber);
    } else {
      history.push('/private/venues');
    }
  }, [currentPageListNumber, selectedLuxuryBox]);

  const renderEditButton = (luxuryBox: LuxuryBox): JSX.Element => {
    return (
      <IconButton
        color="inherit"
        aria-label="edit"
        onClick={() => {
          setSelectedLuxuryBox(luxuryBox);
          history.push('luxury-boxes/edit');
        }}
      >
        <Edit />
      </IconButton>
    );
  };

  const renderPaymentButton = (luxuryBox: LuxuryBox): JSX.Element => {
    return (
      <IconButton
        color="inherit"
        aria-label="payment"
        onClick={() => {
          setSelectedLuxuryBox(luxuryBox);
          history.push(`luxury-boxes/${luxuryBox.id}/movements`);
        }}
      >
        <Payment />
      </IconButton>
    );
  };

  const renderContent = (paginationData: LuxuryBox[]) => {
    return (
      <>
        {paginationData.map((luxuryBox: LuxuryBox) => (
          <TableRow key={luxuryBox.id}>
            <TableCell component="th" scope="row">
              {luxuryBox.floor}
            </TableCell>
            <TableCell component="th" scope="row">
              {luxuryBox.identifierOnFloor || '-'}
            </TableCell>
            <TableCell component="th" scope="row">
              <BooleanIcon value={luxuryBox.usesCheckingAccount} showText />
            </TableCell>
            <TableCell component="th" scope="row">
              {formatMoneyAuto(luxuryBox.balance ?? 0)}
            </TableCell>
            <TableCell>
              {luxuryBox.usesCheckingAccount
                ? renderPaymentButton(luxuryBox)
                : t('listLuxuryBoxes-page-payment-doesnt-apply')}
            </TableCell>
            <TableCell>{renderEditButton(luxuryBox)}</TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  return (
    <>
      <Button
        className="btn-50"
        variant="contained"
        color="primary"
        onClick={() => {
          history.push('luxury-boxes/create');
        }}
      >
        {t('listLuxuryBoxes-page-create')}
      </Button>
      <TableContainer className="table-container" component={Paper}>
        <Table>
          <TableHead className="table-header">
            <TableRow>
              <TableCell>{t('listLuxuryBoxes-page-floor')}</TableCell>
              <TableCell>{t('listLuxuryBoxes-page-identifierOnFloor')}</TableCell>
              <TableCell>{t('listLuxuryBoxes-page-usesCheckingAccount')}</TableCell>
              <TableCell>{t('listLuxuryBoxes-page-balance')}</TableCell>
              <TableCell>{t('listLuxuryBoxes-page-pay-balance')}</TableCell>
              <TableCell>{t('listLuxuryBoxes-page-edit')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {paginationLuxuryBox?.data && paginationLuxuryBox?.data.length > 0 ? (
              renderContent(paginationLuxuryBox.data)
            ) : (
              <TableRow>
                <TableCell component="th" scope="row">
                  {t('listLuxuryBoxes-page-noLuxuryBoxes')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {paginationLuxuryBox && (
        <TablePagination
          component="div"
          count={paginationLuxuryBox.total}
          page={paginationLuxuryBox.currentPage - 1}
          onPageChange={(_, page) => setCurrentPageListNumber(page + 1)}
          rowsPerPage={paginationLuxuryBox.perPage}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => {
            return `${from}-${to} ${t('pagination-of')} ${count !== -1 ? count : '0'}`;
          }}
        />
      )}
    </>
  );
};
